import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Renderer2,
  Signal,
  SimpleChange,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { combineLatest } from 'rxjs';

import { I18nService } from '@recruitee/i18n';
import { HiddenResultPreview, InterviewResult } from '@recruitee/interview-results-types';
import { getRatingName } from '@recruitee/interview-results-util';
import { Placement } from '@recruitee/placements-types';
import {
  IconComponent,
  InterviewEvaluationRatingComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { getPlacementRatingStats, RatingStats } from './rating-stats';

@Component({
  selector: 'rt-evaluation-meter',
  templateUrl: './evaluation-meter.component.html',
  styleUrls: ['./evaluation-meter.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'state()',
    '(click)': 'dontCloseTooltipOnClick($event)',
  },
  imports: [IconComponent, InterviewEvaluationRatingComponent, RtTooltip],
  hostDirectives: [RtTooltip],
})
export class EvaluationMeterComponent {
  private readonly rtTooltip = inject(RtTooltip, { self: true });

  private readonly i18n = inject(I18nService);

  private readonly renderer = inject(Renderer2);

  public placement = input.required<Placement>();

  public interviewResults = input.required<Record<number, InterviewResult[]>>();

  public hiddenResultPreview = input.required<HiddenResultPreview | undefined>();

  public tooltipText = computed(() => {
    if (this.isRatingHidden() && this.hiddenResultPreview()) {
      return this.i18n.translate('app.candidate.placements.eval_meter.hidden_evals_tooltip', {
        count: this.hiddenResultPreview()!.count,
      });
    }

    if (this.isDefault()) {
      return this.i18n.translate('app.candidate.placements.eval_meter.no_evals_tooltip');
    }

    return this.i18n.translate(
      'app.candidate.placements.eval_meter.has_evals_tooltip',
      this.ratingStats(),
    );
  });

  protected state = computed(() => {
    if (this.isCandidateDisqualified()) {
      return 'candidate-disqualified';
    }

    if (this.isRatingHidden()) {
      return 'rating-hidden';
    }

    if (this.isDefault()) {
      return 'default';
    }

    return getRatingName(this.rating()!);
  });

  protected rating = computed(() => this.placement().positiveRatings ?? null);

  protected isRatingHidden = computed(
    () => this.placement().ratingVisible !== undefined && !this.placement().ratingVisible,
  );

  private isCandidateDisqualified = computed(() => !!this.placement().disqualifyReason?.length);

  private isDefault = computed(() => this.rating() === null || this.isRatingHidden());

  private ratingStats: Signal<RatingStats> = computed(() =>
    getPlacementRatingStats(this.placement(), this.interviewResults()),
  );

  private pathGroup = viewChild.required<ElementRef>('pathGroup');

  private paths: Signal<SVGPathElement[]> = computed(
    () => <SVGPathElement[]>Array.from(this.pathGroup().nativeElement.children).reverse(),
  );

  private onColorChanges = combineLatest([toObservable(this.state), toObservable(this.rating)])
    .pipe(takeUntilDestroyed())
    .subscribe(() => this.markToColorFromFirstToPrc(this.rating()));

  private onTooltipTextChanges = toObservable(this.tooltipText)
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      if (!this.tooltipText().length) {
        return;
      }

      this.rtTooltip.message = this.tooltipText();
      this.rtTooltip.ngOnChanges({
        message: new SimpleChange(this.rtTooltip.message, this.tooltipText(), false),
      });
    });

  protected dontCloseTooltipOnClick(event: MouseEvent): void {
    event.stopImmediatePropagation();
  }

  private markToColorFromFirstToPrc(prcOfPathsToColor: number | null): void {
    /**
     * Ex.: ceil(33 / 100 * 25) = ceil(0.33 * 25) = ceil(8.25) = 9.
     * But, we always want to color at least one path, hence Math.max.
     * We subtract 1, because the index is zero-based.
     * We don't worry about values < 0 and values > 100.
     * For prcOfPathsToColor = null, we don't color any path.
     */
    const lastColoredPathIndex =
      prcOfPathsToColor === null
        ? -1
        : Math.max(1, Math.ceil((prcOfPathsToColor / 100) * this.paths().length)) - 1;

    this.paths().forEach((path, index) => {
      if (index <= lastColoredPathIndex) {
        this.renderer.addClass(path, 'colored');
      } else {
        this.renderer.removeClass(path, 'colored');
      }
    });
  }
}
